<template>
    <div class="notification-form">
        <header>
            <h3>
                {{ title }}
            </h3>

            <h4 @click="showPreview = true">
                Preview Post
            </h4>
        </header>

        <section>
            <div class="input-wrapper">
				<label class="required">
					Notification Title
				</label>

				<input
					type="text"
					v-model="notification.title"
					maxlength="50"
				>

				<div class="input-validation">
					<p class="error" v-if="errors.title">
						{{ errors.title }}
					</p>
					<p class="limit">
						{{ 50 - notification.title.length }}
					</p>
				</div>
			</div>

            <div class="input-wrapper">
				<label class="required">
					Notification Description
				</label>

				<textarea maxlength="50" v-model="notification.description"></textarea>

				<div class="input-validation">
					<p class="error" v-if="errors.description">
						{{ errors.description }}
					</p>
					<p class="limit">
						{{ 50 - notification.description.length }}
					</p>
				</div>
			</div>

            <div class="input-wrapper">
				<label class="required">
					Post Date
				</label>

				<div class="input-group">
					<date-picker
						v-model="notification.date"
						format="YYYY-MM-DD"
						type="date"
						placeholder="Select date"
						:disabled-date="notBeforeDate"
					></date-picker>

					<date-picker
						v-model="notification.time"
						type="time"
						placeholder="Select time"
						:default-value="new Date()"
						:disabled-time="notBeforeTime"
					></date-picker>
						<!-- format="HH:mm"
						value-type="format" -->
				</div>

				<div class="input-validation">
					<p class="error" v-if="errors.dateTime">
						{{ errors.dateTime }}
					</p>
				</div>
			</div>

            <header>
                <label>
                    Add Filter
                </label>

                <span
                    class="clear"
                    :class="{ 'disabled': !hasFilters }"
                    @click="clearFilters()"
                >
                    Clear Filter
                </span>
            </header>

            <div class="input-group">
                <div
                    class="input-wrapper checkbox-wrapper"
                    :class="{ 'active': userFilter.country }"
                >
                    <custom-checkbox
                        label="Country"
                        v-model="userFilter.country"
                        :disabled="false"
                    >
                    </custom-checkbox>
                </div>

                <div
                    class="input-wrapper checkbox-wrapper"
                    :class="{ 'active': userFilter.age }"
                >
                    <custom-checkbox
                        label="Age"
                        v-model="userFilter.age"
                    >
                    </custom-checkbox>
                </div>

                <div
                    class="input-wrapper checkbox-wrapper"
                    :class="{ 'active': userFilter.gender }"
                >
                    <custom-checkbox
                        label="Sex"
                        v-model="userFilter.gender"
                    >
                    </custom-checkbox>
                </div>
            </div>

            <label>
                Select User
            </label>

            <custom-select
                v-model="notification.users"
                :items="userList.data"
                item-key="id"
                item-text="name"
                placeholder="Select user"
                is-searchable
                is-multiple
                pull-up
				:is-loading="countryListLoading"
				@search="searchUser"
				@loadMore="loadMoreUserList"
            >
                <template #selected="{ value }">
                    <div
                        class="tags"
                        v-for="(item, index) in value"
                        :key="index"
                    >
                        {{ item.name }}

                        <span
                            class="remove"
                            @click.stop="removeItem(index, 'users')"
                        >
                            &times;
                        </span>
                    </div>
                </template>
            </custom-select>

            <div v-if="userFilter.country">
                <label>
                    Select Country
                </label>
				<custom-select
					v-model="notification.country"
					:items="countryList.data"
					item-key="country"
					item-text="country"
					placeholder="Select country"
					is-searchable
					is-multiple
					pull-up
					:is-loading="countryListLoading"
					@search="searchCountry"
					@loadMore="loadMoreCountryList"
					@input="resetUserList"
				>
					<template #selected="{ value }">
						<div
							class="tags"
							v-for="(item, index) in value"
							:key="index"
						>
							{{ item.country }}

							<span
								class="remove"
								@click.stop="removeItem(index, 'country')"
							>
								&times;
							</span>
						</div>
					</template>
				</custom-select>
            </div>

            <div v-if="userFilter.age">
                <label>
                    Select Age
                </label>

                <div class="input-group">
                    <input
                        type="number"
                        placeholder="from"
                        v-model="notification.ageFrom"
						@input="setUserAge"
						@change="validateUserAge($event, 'ageFrom')"
						min="1"
                    >

                    <input
                        type="number"
                        placeholder="to"
                        v-model="notification.ageTo"
						@input="setUserAge"
						@change="validateUserAge($event, 'ageTo')"
						:disabled="!parseInt(notification.ageFrom)"
						min="1"
                    >
                </div>

				<div class="input-validation">
					<p class="error" v-if="errors.age">
						{{ errors.age }}
					</p>
				</div>
            </div>

            <div v-if="userFilter.gender">
                <label>
                    Select Sex
                </label>

                <custom-select
                    v-model="notification.gender"
                    :items="[ { value: 'Male' }, { value: 'Female' }, { value: 'Others' } ]"
                    item-key="value"
                    item-text="value"
                    placeholder="Select Gender"
                    pull-up
					is-multiple
					@input="resetUserList"
                >
					<template #selected="{ value }">
						<div
							class="tags"
							v-for="(item, index) in value"
							:key="index"
						>
							{{ item.value }}

							<span
								class="remove"
								@click.stop="removeItem(index, 'gender')"
							>
								&times;
							</span>
						</div>
					</template>
                </custom-select>
            </div>

            <footer>
                <button
                    class="btn-danger"
                    @click="$emit('cancel')"
                >
                    Cancel
                </button>

                <button
                    class="btn-secondary"
                    @click="$emit('post')"
					:disabled="buttonLoading"
                >
                    Post
                </button>
            </footer>
        </section>

        <preview
            v-if="showPreview"
            @close="showPreview = false"
            :avatar="`${URL}img/resources/Chickin/emoji/calm.png`"
            :date="notification.date"
            :time="notification.time"
            :title="notification.title"
            :description="notification.description"
        ></preview>

		<modal
			v-if="modalName.includes('not-empty-form')"
			@close="$store.dispatch('closeModal')"
		>
			<section class="warning" slot="content">
				<span @click="$store.dispatch('closeModal')" class="close">&#10005;</span>
				<h1>{{ modalMessage }}</h1>

				<div class="actions">
					<button @click="$store.dispatch('confirm')" class="btn-primary">Ok</button>
					<button @click="$store.dispatch('closeModal')" class="btn-danger">Cancel</button>
				</div>
			</section>
		</modal>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { mapFields } from 'vuex-map-fields'
import CustomCheckbox from '@/components/utils/CustomCheckbox.vue'
import CustomSelect from '@/components/utils/CustomSelect.vue'
import Modal from '@/components/utils/Modal.vue'
import Preview from './Preview.vue'

export default {
	props: {
		notificationId: {
			type: String,
			default: null
		},

		title: {
			type: String,
			required: true
		}
	},

	created () {
		this.getUserList()
		this.getCountryList()
	},

	data () {
		return {
			showPreview: false,
			URL: process.env.VUE_APP_BASE
		}
	},

	computed: {
		...mapFields('notification', {
			notification: 'notification',
			oldNotification: 'oldNotification',
			errors: 'errors',
			buttonLoading: 'buttonLoading',
			countryList: 'countryList',
			countryListLoading: 'countryListLoading',
			userList: 'userList',
			userListLoading: 'userListLoading',
			userFilter: 'userFilter',
			countryKeyword: 'countryKeyword'
		}),

		...mapGetters({
			modalName: 'modalName'
		}),

		hasFilters () {
			if (this.notification.users.length > 0 ||
                this.userFilter.country ||
                this.userFilter.age ||
                this.userFilter.gender
			) return true

			return false
		},

		modalMessage () {
			const messages = {
				'edit-notification': 'Your changes will be lost.',
				'create-notification': 'Your input will be lost.'
			}

			return messages[this.$route.name]
		}
	},

	watch: {
		'notification.ageTo' (value) {
			const num = value.match(/^\d+$/)
			if (num === null) return

			this.errors.age = ''
			if (parseInt(value) < parseInt(this.notification.ageFrom)) {
				this.errors.age = 'Age range is invalid.'
			}
		}
	},

	methods: {
		validateUserAge (e, key) {
			const num = e.target.value.match(/^\d+$/)
			if (num === null) {
				e.target.value = 0
				this.notification[key] = ''

				if (key === 'ageFrom') {
					this.notification.ageTo = ''

					this.resetUserList()
				}
			}
		},

		notBeforeDate (date) {
			const dateToday = new Date()
			dateToday.setDate(dateToday.getDate() - 1)
			return date < dateToday
		},

		notBeforeTime (date) {
			const dateToday = new Date()
			return date.getHours() < dateToday.getHours()
		},

		removeItem (index, key) {
			this.notification[key].splice(index, 1)

			if (['country', 'gender'].includes(key)) {
				this.resetUserList()
			}
		},

		loadMoreCountry () {
			this.countryList.current_page++
			this.searchCountry()
		},

		resetUserList () {
			this.userList = {
				current_page: 0,
				last_page: 0,
				data: []
			}

			this.getUserList()
		},

		async setUserAge (e) {
			let num = e.target.value.match(/^\d+$/)
			if (num === null) return

			clearTimeout(this.timeout)
			this.timeout = setTimeout(() => {
				if (parseInt(this.notification.ageFrom)) {
					this.resetUserList()
				}
			}, 500)
		},

		async searchUser (e) {
			clearTimeout(this.timeout)
			this.timeout = setTimeout(() => {
				this.userFilter.keyword = e
				this.userList = {
					data: [],
					current_page: 0
				}
				this.getUserList()
			}, 500)
		},

		async searchCountry (e) {
			clearTimeout(this.timeout)
			this.timeout = setTimeout(() => {
				this.countryKeyword = e
				this.countryList = {
					data: [],
					current_page: 0
				}
				this.getCountryList()
			}, 500)
		},

		loadMoreCountryList () {
			const { current_page, last_page } = this.countryList

			if (current_page === last_page) return

			this.getCountryList()
		},

		loadMoreUserList () {
			const { current_page, last_page } = this.userList

			if (current_page === last_page && this.userListLoading) return

			this.getUserList()
		},

		async getUserList () {
			const {
				country,
				ageTo,
				ageFrom,
				gender
			} = this.notification
			let apiQuery = `?page=${++this.userList.current_page}`

			if (this.userFilter.keyword) {
				apiQuery += `&keyword=${this.userFilter.keyword}`
			}

			if (gender.length) {
				apiQuery += `&gender=${gender.map(item => item.value).join()}`
			}

			if (country.length) {
				apiQuery += `&country=${country.map(item => item.country).join()}`
			}

			if (ageFrom && ageFrom <= 100) {
				if (parseInt(ageTo) && parseInt(ageFrom) <= parseInt(ageTo) && parseInt(ageTo) <= 100) {
					apiQuery += `&age_from=${ageFrom}`
					apiQuery += `&age_to=${ageTo}`
				} else if (!parseInt(ageTo)) {
					apiQuery += `&age_from=${ageFrom}`
					apiQuery += `&age_to=${ageFrom}`
				}
			}

			this.$store.dispatch('notification/getUserList', apiQuery)
		},

		async getCountryList () {
			let api = `?page=${++this.countryList.current_page}`

			if (this.countryKeyword) {
				api += `&keyword=${this.countryKeyword}`
			}

			this.$store.dispatch('notification/getCountryList', api)
		},

		clearFilters () {
			this.userFilter = {
				country: false,
				age: false,
				gender: false,
				keyword: ''
			}
		}
	},

	components: {
		CustomCheckbox,
		CustomSelect,
		Preview,
		Modal
	}
}
</script>

<style lang="scss" scoped>
.notification-form {
    width: 600px;
    margin: 20px auto;
    background-color: #fff;
	padding: 0 20px;

    header {
        @include flex-row(space-between, center);

        h3 {
            padding: 20px 0;
            color: $dark1;
        }

        h4 {
            color: $primary;
            cursor: pointer;
        }

        span.clear {
            color: $danger;
            cursor: pointer;
            user-select: none;

            &.disabled {
                color: $dark;
                pointer-events: none;
            }
        }
    }

    section {
        border-top: 1px solid $light;
        padding-top: 10px;

		.input-validation {
			display: flex;

			p.error {
				color: rgb(238, 76, 76);
			}
		}

        label {
            display: block;
            margin: 15px 0;
        }

        input, textarea {
            display: block;
            width: 100%;
            padding: 5px 15px;
        }

        input[type=number] {
            width: 100px;
            margin-right: 10px;
        }

        textarea {
            height: 100px;
        }

        p.limit {
			flex: 1 1 auto;
            text-align: right;
            color: $dark;
        }

        .input-group {
            display: flex;

            > * {
                &:not(:last-child) {
                    margin-right: 10px;
                }
            }

            .input-wrapper {
                flex: 1 1 auto;

                &.checkbox-wrapper {
                    border: 1px solid $dark;
                    background-color: $light1;
                    border-radius: 5px;
                    padding: 5px;

                    &.active {
                        background-color: $primary;

                        span {
                            color: #fff;
                        }
                    }
                }
            }
        }

        footer {
            @include flex-row(flex-end, stretch);
            padding: 20px 0;

            button {
                margin-left: 10px;
            }
        }

    }

    .tags {
        display: inline-block;
        padding: 5px;
        margin-right: 3px;
        margin-bottom: 2px;
        margin-top: 2px;
        background-color: $primary;
        color: #fff;
        border-radius: 3px;
        @include flex-row(space-between, center);

        .remove {
            margin-left: 5px;
            font-size: 1.5em;
            line-height: 1em;
            pointer-events: all;

            &:hover {
                color: $danger;
            }
        }
    }
}
</style>
